import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useFieldArray, useForm } from 'react-hook-form';

import { Container, LoadingState, ErrorState, EmptyState } from '@/components/common';
import { useDiagnosticProblemDetailWithCategories, useUpdateDiagnosticProblem } from '@/hooks/diagnostic.hook';
import { errorToast, successToast } from '@/common/toast';
import { MonacoEditorStyles, TrainingDetailHeader, TrainingDetailView, TrainingDetailEdit } from './components';
import { areStep2BlankLineAnswersFilled } from './utils/step2LineAnswersValidation';

/**
 * 진단 및 대응훈련 문제 상세 컴포넌트
 * 
 * VS Code 스타일의 인터페이스로 문제를 미리 풀어볼 수 있는 페이지입니다.
 * 1단계와 2단계 힌트를 보고 정답을 제출하며, 정답 확인도 가능합니다.
 */
const TrainingDetailPage = () => {
  const { problemId } = useParams();
  const navigate = useNavigate();
  
  // 수정 모드 상태
  const [isEditMode, setIsEditMode] = useState(false);

  // authAtom 사용자 정보 조회
  const { userId } = JSON.parse(sessionStorage.getItem('authAtom')) || { userId: 'admin' };
  // 문제 상세 정보 조회
  const { data, isLoading, error } = useDiagnosticProblemDetailWithCategories(problemId);
  const training = data?.detail || {};
  const editCategories = data?.categories || [];
  const editSubCategories = data?.subCategories || [];

  // 언어 선택 상태
  const [languageId, setLanguageId] = useState(training?.language?.id || '');
  const [selectedCategoryId, setSelectedCategoryId] = useState(training?.category?.id || null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(training?.category?.subCategory?.id || '');

  // 단계 선택 (1: 1단계, 2: 2단계)
  const [activeStep, setActiveStep] = useState(1);

  // 단계 변경 핸들러
  const handleStepChange = useCallback((event, newValue) => {
    setActiveStep(newValue);
  }, []);

  // 뒤로가기 핸들러
  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // 수정 모드용 폼 관리
  const {
    control: editControl,
    handleSubmit: handleEditSubmit,
    formState: { errors: editErrors, isDirty: isEditDirty },
    watch: editWatch,
    getValues: editGetValues,
    setValue: editSetValue,
    reset: editReset,
  } = useForm({
    defaultValues: {
      categoryId: '',
      subCategoryId: '',
      difficulty: '',
      languageId: '',
      hint1: '',
      hint2: '',
      step1Files: [],
      step2Files: [],
    },
  });

  // 수정 모드용 필드 배열
  const { fields: editStep1Fields, append: editAppendStep1, remove: editRemoveStep1 } = useFieldArray({
    control: editControl,
    name: 'step1Files',
  });

  const { fields: editStep2Fields, append: editAppendStep2, remove: editRemoveStep2 } = useFieldArray({
    control: editControl,
    name: 'step2Files',
  });

  // 문제 수정 훅
  const updateTrainingMutation = useUpdateDiagnosticProblem(problemId);

  // 수정 모드 진입 핸들러
  const handleEnterEditMode = useCallback(() => {
    if (!training) return;

    // 기존 데이터를 폼에 채우기
    const step1Files = (training.vulFiles || []).map(file => {
      let linesValue = '';
      if (file.lineNum) {
        if (Array.isArray(file.lineNum)) {
          linesValue = file.lineNum.join(',');
        } else {
          linesValue = String(file.lineNum);
        }
      }
      return {
        fileName: file.fileName || '',
        content: file.content || '',
        lines: linesValue,
      };
    });

    const step2Files = (training.secFiles || []).map(file => {
      const keyword = file.keyword || '';
      const lineAnswers = keyword
        // "/"를 기준으로 분리, ","는 공백으로 대체하는 로직
        ? keyword.replace(/,/g, ' ').split('/').map((s) => s.trim()).filter(Boolean)
        : [];
      return {
        fileName: file.fileName || '',
        content: file.content || '',
        word: keyword,
        lineAnswers,
      };
    });

    // 카테고리 정보 설정 (training에서 가져온 정보 기반)
    const initialLanguageId = training.language?.id || '';
    const initialCategoryId = training.category?.id || null;
    const initialSubCategoryId = training.category?.subCategory?.id || '';

    setLanguageId(initialLanguageId);
    setSelectedCategoryId(initialCategoryId);
    setSelectedSubCategoryId(initialSubCategoryId);

    editReset({
      categoryId: initialCategoryId || '',
      subCategoryId: initialSubCategoryId || '',
      difficulty: training.level || '',
      languageId: initialLanguageId,
      hint1: training.vulDesc || '',
      hint2: training.secDesc || '',
      step1Files,
      step2Files,
    });

    setIsEditMode(true);
  }, [training, editReset]);

  // 수정 모드 취소 핸들러
  const handleCancelEditMode = useCallback(() => {
    setIsEditMode(false);
  }, []);

  // 수정 모드용 카테고리 변경 핸들러
  const handleEditCategoryChange = useCallback((categoryId) => {
    setSelectedCategoryId(categoryId);
    setSelectedSubCategoryId(null);
    editSetValue('subCategoryId', '');
  }, [editSetValue]);

  // 수정 모드용 서브카테고리 변경 핸들러
  const handleEditSubCategoryChange = useCallback((subCategoryId) => {
    setSelectedSubCategoryId(subCategoryId);
  }, []);

  // 수정 모드용 언어 변경 핸들러
  const handleEditLanguageChange = useCallback((langId) => {
    setLanguageId(langId);
  }, []);

  // 수정 모드 제출 핸들러
  const onSubmitEdit = useCallback(
    async (data) => {
      try {
        const { difficulty, trainingName, step1Files, step2Files, ...restData } = data; // trainingName 제거

        if (!areStep2BlankLineAnswersFilled(step2Files)) {
          errorToast('입력하지 않은 2단계 정답이 있습니다.');
          return;
        }

        // step1Files를 files1과 answer1로 분리
        const files1 = step1Files
          .map(file => ({
            fileName: file.fileName.trim(),
            content: file.content,
            lineNum: file.lines !== null && file.lines !== '' ? file.lines : null,
          }));

        const files2 = step2Files
          .map(file => {
            const lineAnswersFiltered = Array.isArray(file.lineAnswers)
              ? file.lineAnswers.filter(Boolean)
              : [];
            const answers = lineAnswersFiltered.length > 0
              ? lineAnswersFiltered.join('/')
              : (file.word || null);
            return {
              fileName: file.fileName.trim(),
              content: file.content,
              // answers의 공백을 ,로 치환하여 keyword에 할당
              keyword: typeof answers === 'string' ? answers.replace(/\s+/g, ',') : answers,
            };
          });

        const requestData = {
          problemId: parseInt(problemId),
          languageId: languageId,
          subCategoryId: parseInt(data.subCategoryId),
          level: data.difficulty,
          createdBy: userId,
          vulDesc: data.hint1,
          secDesc: data.hint2,
          vulFiles: files1,
          secFiles: files2,
        }

        await updateTrainingMutation.mutateAsync({ requestData });

        successToast('문제가 수정되었습니다. 풀어보기 후 승인 상태로 전환해주세요');
        navigate(-1);
      } catch (error) {
        errorToast(`문제 수정에 실패했습니다. - ${error.response?.data?.msg}`);
      }
    },
    [problemId, languageId, userId, navigate, updateTrainingMutation]
  );

  // problemId 변경 시 상태 초기화
  useEffect(() => {
    setActiveStep(1);
    setIsEditMode(false);
  }, [problemId]);

  // 로딩 중
  if (isLoading) {
    return <LoadingState title='문제 미리 풀어보기' />;
  }

  // 에러 발생
  if (error) {
    return <ErrorState title='문제 미리 풀어보기' message='문제 정보를 불러오는 중 오류가 발생했습니다.' onGoBack={handleGoBack} />;
  }

  // 데이터 없음
  if (!training) {
    return <EmptyState title='문제 미리 풀어보기' message='문제 정보를 찾을 수 없습니다.' onGoBack={handleGoBack} />;
  }

  return (
    <>
      <MonacoEditorStyles />
      <Container title={isEditMode ? `${training.problemId}번 문제 수정` : `${training.problemId}번 문제 미리 풀어보기`}>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 200px)' }}>
          {/* 공통 헤더 */}
          <TrainingDetailHeader
            training={training}
            onGoBack={handleGoBack}
            isEditMode={isEditMode}
            onEnterEditMode={handleEnterEditMode}
            onCancelEditMode={handleCancelEditMode}
            onSubmitEdit={handleEditSubmit(onSubmitEdit)}
            isEditDirty={isEditDirty}
          />

          {/* 수정 모드 또는 기본 상세보기 */}
          {isEditMode ? (
            <TrainingDetailEdit
              control={editControl}
              errors={editErrors}
              watch={editWatch}
              getValues={editGetValues}
              setValue={editSetValue}
              step1Fields={editStep1Fields}
              step2Fields={editStep2Fields}
              appendStep1={editAppendStep1}
              appendStep2={editAppendStep2}
              removeStep1={editRemoveStep1}
              removeStep2={editRemoveStep2}
              categories={editCategories}
              subCategories={editSubCategories}
              selectedCategoryId={selectedCategoryId}
              onCategoryChange={handleEditCategoryChange}
              onSubCategoryChange={handleEditSubCategoryChange}
              onLanguageChange={handleEditLanguageChange}
            />
          ) : (
            <TrainingDetailView
              training={training}
              step={activeStep}
              onStepChange={handleStepChange}
            />
          )}
        </Box>
      </Container>
    </>
  );
};

export default TrainingDetailPage;
